import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] text-[13px] font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-violet-deep text-white shadow-[0_1px_0_hsl(0_0%_100%/0.15)_inset,0_4px_12px_-4px_hsl(var(--violet)/0.5)] hover:bg-violet font-semibold',
        secondary:
          'bg-bg-1 text-text-1 border border-line-1 hover:border-line-2 hover:bg-bg-2 shadow-card',
        ghost: 'text-text-2 hover:text-text-1 hover:bg-bg-2',
        outline: 'border border-line-2 text-text-1 hover:border-line-3 hover:bg-bg-2',
        destructive:
          'bg-rose text-white hover:bg-rose/90 font-semibold shadow-[0_4px_12px_-4px_hsl(var(--rose)/0.4)]',
        link: 'text-violet-deep underline-offset-4 hover:underline',
      },
      size: {
        md: 'h-11 px-3.5 sm:h-9',
        sm: 'h-11 px-3 text-[12px] sm:h-8',
        lg: 'h-11 px-5',
        icon: 'size-11 sm:size-9',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
